import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  formatDate,
  formatEuro,
  isoToDateInput,
  parseDateToIso,
  parseEuroToCentimes,
} from '../lib/format';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';

export function TresorerieAcomptesPage(): JSX.Element {
  const [nonRattaches, setNonRattaches] = useState(true);
  const [creerOpen, setCreerOpen] = useState(false);
  const [rattacherAcompteId, setRattacherAcompteId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['acomptes', { nonRattaches }],
    queryFn: () => api.acomptes.list(nonRattaches ? { non_rattaches: 'true' } : {}),
  });

  return (
    <div className="max-w-7xl space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Acomptes</h1>
          <p className="text-xs text-slate-500 mt-1">
            Acomptes reçus avant facture finale, rattachés manuellement à une vente.
          </p>
        </div>
        <Button onClick={(): void => setCreerOpen(true)}>Créer un acompte</Button>
      </header>

      <label className="inline-flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={nonRattaches}
          onChange={(e): void => setNonRattaches(e.target.checked)}
          className="rounded border-slate-600 bg-slate-800"
        />
        Non rattachés uniquement
      </label>

      {isLoading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/50 border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Client</th>
                <th className="py-2 px-3 text-right">HT</th>
                <th className="py-2 px-3 text-right">TVA</th>
                <th className="py-2 px-3 text-right">TTC</th>
                <th className="py-2 px-3">Vente liée</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((a) => (
                <tr key={a.id} className="border-b border-slate-800/50">
                  <td className="py-2 px-3">{formatDate(a.date_reception)}</td>
                  <td className="py-2 px-3">{a.contact_societe ?? a.contact_nom}</td>
                  <td className="py-2 px-3 text-right">{formatEuro(a.montant_ht_centimes)}</td>
                  <td className="py-2 px-3 text-right">{formatEuro(a.montant_tva_centimes)}</td>
                  <td className="py-2 px-3 text-right">{formatEuro(a.montant_ttc_centimes)}</td>
                  <td className="py-2 px-3 text-slate-400">
                    {a.vente_numero_henrri ??
                      (a.vente_id !== null ? `#${String(a.vente_id)}` : '—')}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {a.vente_id === null && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(): void => setRattacherAcompteId(a.id)}
                      >
                        Rattacher
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-sm text-slate-500">
                    Aucun acompte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <CreerAcompteModal isOpen={creerOpen} onClose={(): void => setCreerOpen(false)} />
      {rattacherAcompteId !== null && (
        <RattacherAcompteModal
          acompteId={rattacherAcompteId}
          onClose={(): void => setRattacherAcompteId(null)}
        />
      )}
    </div>
  );
}

function CreerAcompteModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [contactQuery, setContactQuery] = useState('');
  const [contactId, setContactId] = useState<number | null>(null);
  const [contactNom, setContactNom] = useState<string>('');
  const [dateReception, setDateReception] = useState(isoToDateInput(new Date().toISOString()));
  const [montantTtc, setMontantTtc] = useState('');
  const [tauxTva, setTauxTva] = useState('20,00');
  const [mode, setMode] = useState<'Virement' | 'Cheque' | 'Especes'>('Virement');
  const [error, setError] = useState<string | null>(null);

  const { data: contacts } = useQuery({
    queryKey: ['contacts-search', contactQuery],
    queryFn: () => api.contacts.list({ q: contactQuery, page_size: 8 }),
    enabled: contactQuery.length >= 2,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (contactId === null) throw new Error('Sélectionnez un contact');
      const dateIso = parseDateToIso(dateReception);
      const ttc = parseEuroToCentimes(montantTtc);
      const tauxFloat = Number.parseFloat(tauxTva.replace(',', '.'));
      if (dateIso === null) throw new Error('Date invalide');
      if (ttc === null || ttc <= 0) throw new Error('Montant TTC invalide');
      if (!Number.isFinite(tauxFloat) || tauxFloat < 0) throw new Error('Taux TVA invalide');
      const taux = Math.round(tauxFloat * 100); // 20 → 2000
      // HT = TTC / (1 + tauxPct/100) ; on calcule en centimes
      const ht = Math.round((ttc * 10_000) / (10_000 + taux));
      const tva = ttc - ht;
      return api.acomptes.create({
        contact_id: contactId,
        devis_id: null,
        numero_acompte_henrri: null,
        date_reception: dateIso,
        mode_paiement: mode,
        taux_tva: taux,
        montant_ht_centimes: ht,
        montant_tva_centimes: tva,
        montant_ttc_centimes: ttc,
        notes: null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['acomptes'] });
      onClose();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Créer un acompte"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={(): void => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Créer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Input
            label="Contact (taper 2+ caractères)"
            value={contactNom !== '' ? contactNom : contactQuery}
            onChange={(e): void => {
              setContactQuery(e.target.value);
              setContactId(null);
              setContactNom('');
            }}
          />
          {contactId === null && contacts !== undefined && contactQuery.length >= 2 && (
            <ul className="mt-1 bg-slate-800 border border-slate-700 rounded-md max-h-40 overflow-auto">
              {contacts.items.map((c) => (
                <li
                  key={c.id}
                  onClick={(): void => {
                    setContactId(c.id);
                    setContactNom(`${c.societe ?? c.nom} (${c.nom})`);
                    setContactQuery('');
                  }}
                  className="px-3 py-2 text-sm text-slate-200 hover:bg-slate-700 cursor-pointer"
                >
                  {c.societe ?? c.nom} — {c.nom}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Input
          label="Date de réception"
          type="date"
          value={dateReception}
          onChange={(e): void => setDateReception(e.target.value)}
        />
        <Input
          label="Montant TTC (€)"
          value={montantTtc}
          onChange={(e): void => setMontantTtc(e.target.value)}
          placeholder="1 200,00"
        />
        <Input
          label="Taux TVA (%)"
          value={tauxTva}
          onChange={(e): void => setTauxTva(e.target.value)}
        />
        <Select
          label="Mode de paiement"
          value={mode}
          onChange={(e): void => setMode(e.target.value as 'Virement' | 'Cheque' | 'Especes')}
          options={[
            { value: 'Virement', label: 'Virement' },
            { value: 'Cheque', label: 'Chèque' },
            { value: 'Especes', label: 'Espèces' },
          ]}
        />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}

function RattacherAcompteModal({
  acompteId,
  onClose,
}: {
  acompteId: number;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [venteIdStr, setVenteIdStr] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const vId = Number.parseInt(venteIdStr, 10);
      if (!Number.isFinite(vId) || vId <= 0) throw new Error('ID vente invalide');
      return api.acomptes.lierVente(acompteId, { vente_id: vId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['acomptes'] });
      void queryClient.invalidateQueries({ queryKey: ['encaissements'] });
      void queryClient.invalidateQueries({ queryKey: ['encaissements-stats'] });
      onClose();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Rattacher l'acompte à une vente"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={(): void => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Rattacher
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="ID de la vente"
          value={venteIdStr}
          onChange={(e): void => setVenteIdStr(e.target.value)}
          placeholder="42"
        />
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
